/**
 * Admin V3 — Operations / Audit — Table component
 *
 * 7 columns: (expand toggle) | Horodatage | Opérateur | Action (pill)
 *          | Cible (entity_type:entity_id truncated) | Raison | Statut.
 *
 * Per-row expandable detail panel shows:
 *   - intervention_id (if linked)
 *   - ip_address, user_agent, session_id
 *   - error_message (if failed)
 *   - before_state JSON
 *   - after_state JSON
 *
 * Drill-down per entity_type (where a V3 target exists):
 *   - booking   → /admin/bookings/:id (legacy V2 detail, Phase 2.2 precedent)
 *   - driver    → /admin-v3/drivers/:id
 *   - incident  → no drill-down — rendered as text (no incidents/:id route exists)
 *   Other entity types (payment, payout, event, notification, ...) render
 *   entity_id as monospace text only — no drill-down.
 *
 * Failed actions (succeeded=false) get a rose left-border + ✗ status pill.
 *
 * NO mutation surface.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import bookingDetailPath from '../bookings/bookingDetailPath';
import { History, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink, Check, X as XIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  EmptyState,
  SmallPill,
  operatorActionTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const truncate = (s, n = 12) => {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
};

// Map entity_type to V3 (or legacy V2) detail URL. Returns null when no target.
const detailUrlFor = (entityType, entityId) => {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'booking':  return bookingDetailPath(entityId);         // AV3-M2: V3 primary (V2 fallback via flag)
    case 'driver':   return `/admin/drivers/${entityId}`;
    // P0-1c: no /admin-v3/incidents/:id route exists (the detail route was
    // removed — see AdminV3.js). Returning null renders the incident entity as
    // plain text (same no-drill-down fallback as payment/payout/event) instead
    // of linking to the notFound catch-all.
    case 'incident': return null;
    default:         return null;
  }
};

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(7).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ action, locale }) => {
  const meta = [
    action.intervention_id && [t('operations.audit.detail.intervention', locale), action.intervention_id],
    action.ip_address      && [t('operations.audit.detail.ipAddress',    locale), action.ip_address],
    action.user_agent      && [t('operations.audit.detail.userAgent',    locale), action.user_agent],
    action.session_id      && [t('operations.audit.detail.session',      locale), action.session_id],
    action.error_message   && [t('operations.audit.detail.errorMessage', locale), action.error_message],
  ].filter(Boolean);

  return (
    <div className="space-y-3 bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      {meta.length > 0 && (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          {meta.map(([label, value]) => (
            <div key={label} className="text-[12px]">
              <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
              <dd className="text-slate-700 font-mono break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {action.before_state && Object.keys(action.before_state).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
            {t('operations.audit.detail.beforeState', locale)}
          </div>
          <pre className="rounded-lg bg-white border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-48">
{JSON.stringify(action.before_state, null, 2)}
          </pre>
        </div>
      )}

      {action.after_state && Object.keys(action.after_state).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
            {t('operations.audit.detail.afterState', locale)}
          </div>
          <pre className="rounded-lg bg-white border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto max-h-48">
{JSON.stringify(action.after_state, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const OperationsAuditTable = ({ actions, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-operations-audit-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('operations.audit.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-operations-audit-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('operations.audit.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={History}
      title={t('operations.audit.empty.title', locale)}
      desc={t(isFilterActive ? 'operations.audit.empty.descFilter' : 'operations.audit.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('operations.audit.col.timestamp', locale),
                t('operations.audit.col.operator', locale),
                t('operations.audit.col.action', locale),
                t('operations.audit.col.target', locale),
                t('operations.audit.col.reason', locale),
                t('operations.audit.col.status', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : actions.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              actions.map((a) => {
                const key = a.action_id || `${a.timestamp}-${a.operator_id}-${a.entity_id}`;
                const isExpanded = Boolean(expanded[key]);
                const actionMeta = operatorActionTypeMeta[a.action_type] || {
                  label: a.action_type || '—',
                  pill: 'bg-slate-100 text-slate-700 border border-slate-200',
                };
                const failedTone = a.succeeded === false;
                const targetUrl = detailUrlFor(a.entity_type, a.entity_id);
                const targetText = `${a.entity_type || '—'}: ${truncate(a.entity_id, 12)}`;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        failedTone && 'bg-rose-50/30'
                      )}
                      style={failedTone ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      {/* Expand toggle */}
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded
                            ? t('operations.audit.detail.hide', locale)
                            : t('operations.audit.detail.show', locale)}
                          data-testid={`adminv3-operations-audit-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Horodatage */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(a.timestamp, locale)}
                      </td>

                      {/* Opérateur */}
                      <td className="px-4 py-3.5 text-slate-700 text-[12px] font-mono break-all max-w-[200px]">
                        {a.operator_id || '—'}
                      </td>

                      {/* Action pill */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={actionMeta} />
                      </td>

                      {/* Cible */}
                      <td className="px-4 py-3.5 font-mono text-[12px]">
                        {targetUrl ? (
                          <Link
                            to={targetUrl}
                            className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 hover:underline"
                            data-testid={`adminv3-operations-audit-target-${key}`}
                          >
                            {targetText}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-slate-500">{targetText}</span>
                        )}
                      </td>

                      {/* Raison */}
                      <td className="px-4 py-3.5 text-slate-600 text-[12px] max-w-[260px] truncate">
                        {a.reason || '—'}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        {failedTone ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                            <XIcon className="h-3 w-3" />
                            {t('operations.audit.status.failed', locale)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <Check className="h-3 w-3" />
                            {t('operations.audit.status.success', locale)}
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Expandable detail panel */}
                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={7} className="px-4 py-2">
                          <DetailPanel action={a} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OperationsAuditTable;
